import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import debounce from 'lodash/debounce';
import { flexRender } from '@tanstack/react-table';

// LOCAL VARIABLES
import { ROW_HEIGHT, PLACEHOLDER } from '../../variables';

// STYLES
import styles from './tableRow.module.scss';

const TableRow = forwardRef(
  (
    {
      index,
      placeholder = PLACEHOLDER,
      row,
      rowVirtualizer,
      rowHeight = ROW_HEIGHT,
      rowWrapper: Wrapper = ({ children }) => <>{children}</>,
      setRowHeight,
      virtualRow
    },
    ref
  ) => {
    // STATE
    const [resizeRowTrigger, setResizeRowTrigger] = useState(false);

    // REFS
    const rowRef = useRef({});

    // EFFECTS
    useEffect(() => {
      if (rowRef?.current) {
        const height = rowRef.current.clientHeight;
        if (setRowHeight) {
          setRowHeight(index, height, ref);
        }
      }
    }, [index, ref, resizeRowTrigger, setRowHeight]);

    // CALLBACKS
    const handleResizeRow = useCallback(
      debounce(() => {
        setResizeRowTrigger(prev => !prev);
      }, 100),
      []
    );

    const measureElement = useCallback(node => {
      if (node) {
        rowVirtualizer.measureElement(node);
      }
    }, [rowVirtualizer]);

    return (
      <tr
        data-index={index}
        ref={measureElement}
        key={row.id}
        style={{
          top: virtualRow.start,
          minHeight: rowHeight,
        }}
        className={clsx(
          'tr table-row d-flex position-absolute w-100',
          styles.row
        )}
      >
        <Wrapper record={row.original || {}} resizeRow={handleResizeRow} rowNumber={index + 1}>
          {row.getVisibleCells().map(cell => {
            const width = cell.column.getSize();
            const value = cell.getValue();
            const valueProp = ![undefined, null, ''].includes(value) ? value : placeholder;
            const { cellClassName, className, id } = cell.column.columnDef;

            return (
              <td
                key={cell.id}
                style={{
                  flexGrow: id !== 'actions' && width,
                  flexBasis: width,
                  width,
                }}
                className={clsx(
                  'td d-flex align-items-center table-col',
                  index % 2 === 0 && 'bg-gray-000',
                  styles.td,
                  className,
                  cellClassName
                )}
              >
                <div className={clsx(
                  'w-100 table-cell',
                  id === 'actions' && 'text-center',
                  styles.cell
                )}>
                  {flexRender(
                    cell.column.columnDef.cell,
                    {
                      ...cell.getContext(),
                      column: {
                        ...cell.column,
                        ...cell.column.columnDef,
                      },
                      getValue: () => valueProp,
                      placeholder,
                      value: cell.getValue()
                    }
                  )}
                </div>
              </td>
            )
          })}
        </Wrapper>
      </tr>
    );
  }
);

export default TableRow;
